/* v3.6s — System Logs
   Admin-only. Two panels:
     1. Live-Launch Reset — one-shot archive + counter reset before go-live.
     2. Test History Browser — read-only view of every archived batch.
   Wired to POST /api/admin/live-launch-reset and GET /api/admin/test-history*.
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Archive, RefreshCcw, ShieldAlert, ChevronRight, Database, FileText,
  Truck, Receipt, Layers, DollarSign, Coins, Plus, Mail, Clock, CheckCircle2, AlertTriangle, RotateCcw, XCircle, Send,
} from "lucide-react";

const KIND_META = {
  job:            { label: "Jobs",          Icon: Truck },
  docket:         { label: "Dockets",       Icon: Receipt },
  invoice:        { label: "Invoices",      Icon: FileText },
  batch:          { label: "Batches",       Icon: Layers },
  cash_sale:      { label: "EFT Sales",     Icon: DollarSign },
  quote_request:  { label: "Quotes",        Icon: FileText },
  payment:        { label: "Payments",      Icon: Coins },
  topup:          { label: "Top-ups",       Icon: Plus },
};

export default function SystemLogs() {
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [kind, setKind] = useState("");
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  // Daily-backup state (v3.6t)
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupMailbox, setBackupMailbox] = useState("");
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupDay, setBackupDay] = useState("");
  const [savingMailbox, setSavingMailbox] = useState(false);
  // Monthly job auto-archive (v3.9)
  const [archiveInfo, setArchiveInfo] = useState(null);
  const [archiveRunning, setArchiveRunning] = useState(false);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [recallingId, setRecallingId] = useState("");

  const loadPreview = async () => {
    try {
      const [{ data: prev }, { data: bs }, { data: logs }, { data: bs_settings }] = await Promise.all([
        api.get("/admin/live-launch-preview"),
        api.get("/admin/test-history/batches"),
        api.get("/admin/daily-backup/logs?limit=50"),
        api.get("/admin/backups-settings"),
      ]);
      setPreview(prev);
      setBatches(bs);
      if (bs.length && !selectedBatch) setSelectedBatch(bs[0].batch_id);
      setBackupLogs(logs);
      setBackupMailbox(bs_settings.mailbox || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load system logs");
    }
  };
  useEffect(() => { loadPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedBatch) { setRows([]); return; }
    (async () => {
      setRowsLoading(true);
      try {
        const params = { batch_id: selectedBatch, limit: 500 };
        if (kind) params.kind = kind;
        const { data } = await api.get("/admin/test-history", { params });
        setRows(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load history");
      } finally {
        setRowsLoading(false);
      }
    })();
  }, [selectedBatch, kind]);

  const runReset = async () => {
    if (confirmText !== "LIVE-LAUNCH") { toast.error("Type LIVE-LAUNCH exactly to confirm"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/admin/live-launch-reset", { confirm: "LIVE-LAUNCH" });
      setResetResult(data);
      toast.success(`Archived ${data.total_moved} record${data.total_moved === 1 ? "" : "s"} — ready to go live`);
      setConfirmText("");
      await loadPreview();
      setSelectedBatch(data.batch_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setRunning(false);
    }
  };

  const runBackupNow = async () => {
    // Blank = today; pick a date to re-send an earlier day's summary.
    setBackupRunning(true);
    try {
      const { data } = await api.post(`/admin/daily-backup/run${backupDay ? `?day=${backupDay}` : ""}`);
      if (data.status === "sent") {
        toast.success(`Sent to ${data.recipients.length} recipient${data.recipients.length === 1 ? "" : "s"}`);
      } else {
        toast.error(data.error || "Backup failed");
      }
      await loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup failed");
    } finally {
      setBackupRunning(false);
    }
  };

  const saveMailbox = async () => {
    setSavingMailbox(true);
    try {
      await api.post("/admin/backups-settings", { mailbox: backupMailbox });
      toast.success("Backups mailbox saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingMailbox(false);
    }
  };

  const currentBatch = useMemo(
    () => batches.find((b) => b.batch_id === selectedBatch),
    [batches, selectedBatch],
  );

  const loadArchive = async (q = "") => {
    setArchiveLoading(true);
    try {
      const [{ data: info }, { data: jobs }] = await Promise.all([
        api.get("/admin/archive-old-jobs/preview"),
        api.get("/admin/archived-jobs", { params: q ? { search: q, limit: 200 } : { limit: 200 } }),
      ]);
      setArchiveInfo(info);
      setArchivedJobs(jobs);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load archive");
    } finally {
      setArchiveLoading(false);
    }
  };
  useEffect(() => { loadArchive(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runArchiveNow = async () => {
    setArchiveRunning(true);
    try {
      const { data } = await api.post("/admin/archive-old-jobs/run");
      toast.success(`Archived ${data.archived} finished job${data.archived === 1 ? "" : "s"} older than ${data.cutoff}`);
      await loadArchive(archiveSearch);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally {
      setArchiveRunning(false);
    }
  };

  const recallJob = async (jobId) => {
    setRecallingId(jobId);
    try {
      const { data } = await api.post(`/admin/archived-jobs/${jobId}/recall`);
      toast.success(data.already_live ? "Job was already live — cleared stale archive copy" : "Job recalled to the live board");
      await loadArchive(archiveSearch);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recall failed");
    } finally {
      setRecallingId("");
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="system-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tag flex items-center gap-1.5">
            <ShieldAlert className="w-3 h-3" />Admin · System Logs
          </div>
          <h1 className="heading text-2xl font-black uppercase mt-1">System Logs</h1>
          <p className="text-xs text-muted-industrial mt-1">
            Live-launch archive controls and read-only browser of every prior test data snapshot.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadPreview}
          data-testid="system-logs-refresh"
          className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest"
        >
          <RefreshCcw className="w-3 h-3 mr-1.5" />Refresh
        </Button>
      </div>

      {/* Monthly Job Auto-Archive panel (v3.9) */}
      <div className="border border-industrial bg-surface p-4" data-testid="job-archive-panel">
        <div className="flex items-start gap-3">
          <Archive className="w-5 h-5 text-primary-orange shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 className="heading text-sm font-black uppercase tracking-wide">Job Archive</h2>
            <p className="text-xs text-muted-industrial mt-1">
              Finished jobs older than the previous calendar month are archived automatically every
              night so the Dispatch board and database stay fast. Nothing is deleted — recall any job below.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="text-xs">
                <span className="text-muted-industrial">Cutoff:</span>{" "}
                <span className="mono text-primary-orange" data-testid="archive-cutoff">{archiveInfo?.cutoff || "—"}</span>
                <span className="text-muted-industrial"> (archives jobs before this date)</span>
              </div>
              <div className="text-xs">
                <span className="text-muted-industrial">Eligible now:</span>{" "}
                <span className="font-bold" data-testid="archive-eligible">{archiveInfo?.eligible ?? "—"}</span>
              </div>
              <div className="text-xs">
                <span className="text-muted-industrial">Archived total:</span>{" "}
                <span className="font-bold" data-testid="archive-total">{archiveInfo?.total_archived ?? "—"}</span>
              </div>
              <Button
                variant="outline"
                onClick={runArchiveNow}
                disabled={archiveRunning}
                data-testid="archive-run-now"
                className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest"
              >
                <Archive className="w-3 h-3 mr-1.5" />{archiveRunning ? "Archiving…" : "Archive now"}
              </Button>
            </div>

            {/* Archived jobs browser + recall */}
            <div className="mt-4 border-t border-industrial pt-3">
              <div className="flex items-center gap-2">
                <Input
                  value={archiveSearch}
                  onChange={(e) => setArchiveSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadArchive(archiveSearch); }}
                  placeholder="Search site, customer, mix, PO or order #…"
                  data-testid="archive-search-input"
                  className="rounded-none bg-transparent border-industrial h-8 text-xs max-w-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => loadArchive(archiveSearch)}
                  data-testid="archive-search-btn"
                  className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest h-8"
                >
                  <RefreshCcw className="w-3 h-3 mr-1.5" />Search
                </Button>
              </div>
              <div className="mt-3 max-h-80 overflow-auto border border-industrial" data-testid="archived-jobs-list">
                <table className="w-full text-xs">
                  <thead className="bg-black/20 text-muted-industrial uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-semibold">Date</th>
                      <th className="text-left p-2 font-semibold">Customer / Site</th>
                      <th className="text-left p-2 font-semibold">Mix / Product</th>
                      <th className="text-right p-2 font-semibold">Qty</th>
                      <th className="text-left p-2 font-semibold">Status</th>
                      <th className="text-right p-2 font-semibold">Recall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archiveLoading && (
                      <tr><td colSpan={6} className="p-4 text-center text-muted-industrial">Loading…</td></tr>
                    )}
                    {!archiveLoading && archivedJobs.length === 0 && (
                      <tr><td colSpan={6} className="p-4 text-center text-muted-industrial" data-testid="archived-jobs-empty">No archived jobs{archiveSearch ? " match your search" : " yet"}.</td></tr>
                    )}
                    {!archiveLoading && archivedJobs.map((j) => (
                      <tr key={j.id} className="border-t border-industrial/60 hover:bg-white/5" data-testid={`archived-job-row-${j.id}`}>
                        <td className="p-2 mono whitespace-nowrap">{j.date || "—"}</td>
                        <td className="p-2">
                          <div className="font-semibold truncate max-w-[200px]">{j.customer_name || "—"}</div>
                          <div className="text-muted-industrial truncate max-w-[200px]">{j.site_name || ""}</div>
                        </td>
                        <td className="p-2 mono">{j.mix_code || j.product || "—"}</td>
                        <td className="p-2 text-right mono">{j.quantity_m3 ?? "—"}</td>
                        <td className="p-2 uppercase text-[10px] text-muted-industrial">{j.status || "—"}</td>
                        <td className="p-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => recallJob(j.id)}
                            disabled={recallingId === j.id}
                            data-testid={`recall-job-${j.id}`}
                            className="rounded-none border-primary-orange text-primary-orange bg-transparent uppercase text-[10px] tracking-widest h-7"
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />{recallingId === j.id ? "…" : "Recall"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live-Launch Reset panel */}
      <div className="border-2 border-red-800 bg-red-950/10 p-4" data-testid="live-launch-panel">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-widest text-red-500 font-bold">Danger Zone</div>
            <h2 className="heading text-lg font-black uppercase mt-0.5">Live-Launch Reset</h2>
            <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
              One click archives every operational record (jobs, dockets, invoices, batches, EFT sales,
              quote requests, payments, top-ups) into <span className="mono text-primary-orange">test_history</span>,
              wipes them from the live tables, and resets your Invoice / Docket counters to zero — so day-one
              of live production starts at Invoice #001 and a clean Dispatch board.
              <br />Reference data (users, customers, mixes, trucks, drivers, plants, settings, price rules) is untouched.
            </p>

            {preview && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" data-testid="live-launch-preview-counts">
                {Object.entries(KIND_META).map(([k, meta]) => {
                  const col = {
                    job: "jobs", docket: "dockets", invoice: "invoices",
                    batch: "batches", cash_sale: "cash_sales",
                    quote_request: "quote_requests", payment: "payments", topup: "topups",
                  }[k];
                  const n = preview.counts?.[col] || 0;
                  return (
                    <div key={k} className="border border-red-900/50 bg-black/40 p-2" data-testid={`preview-count-${k}`}>
                      <div className="flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-red-500">
                        <meta.Icon className="w-2.5 h-2.5" />{meta.label}
                      </div>
                      <div className="mono text-2xl font-black text-ink mt-0.5">{n}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {preview && (
              <div className="mt-3 mono text-[10px] text-muted-industrial">
                Next Invoice # will be <span className="text-primary-orange font-bold">1</span> after reset
                (currently {preview.current_invoice_seq + 1}) ·
                Next EFT Sale # <span className="text-primary-orange font-bold">1</span>
                (currently {preview.current_cash_sale_seq + 1}).
              </div>
            )}

            <div className="mt-4">
              <Button
                onClick={() => setResetOpen(true)}
                disabled={!preview || preview.total === 0}
                data-testid="open-live-launch"
                className="rounded-none bg-red-600 hover:bg-red-500 text-white uppercase tracking-widest text-xs font-bold"
              >
                <Archive className="w-3.5 h-3.5 mr-1.5" />
                {preview?.total ? `Archive ${preview.total} record${preview.total === 1 ? "" : "s"} & Reset counters` : "Nothing to archive — you're clean"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Restore Archived Day panel — v3.6w */}
      <RestoreDayPanel />

      {/* Void a Day's Invoices panel — v3.6z */}
      <VoidDayPanel />

      {/* Email delivery health — provider + live self-test */}
      <EmailDeliveryPanel />

      {/* Daily Backup panel — v3.6t */}
      <div className="border border-industrial bg-surface p-4" data-testid="daily-backup-panel">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-primary-orange shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Automated Backup</div>
            <h2 className="heading text-lg font-black uppercase mt-0.5">Daily Summary Email</h2>
            <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
              Runs automatically every night at <span className="mono text-primary-orange">11:00 PM plant local time</span>.
              Builds a CSV of the day's jobs, dockets, invoices, batches, truck activity and mid-pour top-ups, then emails
              it to every admin plus the mailbox below. Each attempt is logged so you can prove the backup was sent.
            </p>

            <div className="grid md:grid-cols-[1fr_auto] gap-2 items-end mt-4">
              <div>
                <div className="label-tag mb-1.5">Extra Backups Mailbox (comma-separated OK)</div>
                <Input
                  data-testid="backups-mailbox-input"
                  value={backupMailbox}
                  onChange={(e) => setBackupMailbox(e.target.value)}
                  placeholder="accounts@yourcompany.com, backup@yourcompany.com"
                  className="rounded-none bg-transparent border-industrial mono text-xs h-9"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={saveMailbox}
                  disabled={savingMailbox}
                  data-testid="save-mailbox"
                  className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest h-9"
                >{savingMailbox ? "Saving…" : "Save"}</Button>
                <input
                  type="date"
                  value={backupDay}
                  onChange={(e) => setBackupDay(e.target.value)}
                  title="Leave blank for today, or pick a day to re-send"
                  data-testid="backup-day"
                  className="h-9 rounded-none bg-transparent border border-industrial mono text-xs px-2"
                />
                <Button
                  onClick={runBackupNow}
                  disabled={backupRunning}
                  data-testid="run-backup-now"
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold h-9"
                >
                  {backupRunning ? "Sending…" : backupDay ? `Send ${backupDay}` : "Run backup now"}
                </Button>
              </div>
            </div>

            {/* Log table */}
            <div className="mt-4 border border-industrial max-h-64 overflow-y-auto">
              <table className="w-full mono text-xs">
                <thead className="bg-surface-2 sticky top-0">
                  <tr className="border-b border-industrial">
                    <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Status</th>
                    <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">When</th>
                    <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Trigger</th>
                    <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Day</th>
                    <th className="p-2 text-right uppercase text-[9px] tracking-widest text-muted-industrial">Dockets</th>
                    <th className="p-2 text-right uppercase text-[9px] tracking-widest text-muted-industrial">m³ Delivered</th>
                    <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Recipients / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {backupLogs.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-muted-industrial">No backup attempts yet — hit "Run backup now" or wait for the 11 PM cron.</td></tr>
                  )}
                  {backupLogs.filter((l) => l.kind === "daily_backup").map((l) => (
                    <tr key={l.id} className="border-b border-industrial hover:bg-surface-2" data-testid={`backup-log-${l.id}`}>
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest ${
                          l.status === "sent" ? "text-emerald-500" : l.status === "running" ? "text-amber-500" : "text-red-500"
                        }`}>
                          {l.status === "sent" && <CheckCircle2 className="w-3 h-3" />}
                          {l.status === "running" && <Clock className="w-3 h-3" />}
                          {l.status === "failed" && <AlertTriangle className="w-3 h-3" />}
                          {l.status}
                        </span>
                      </td>
                      <td className="p-2 text-muted-industrial tabular-nums">{(l.finished_at || l.started_at || "").replace("T", " ").slice(0, 16)}</td>
                      <td className="p-2 uppercase text-[10px] text-primary-orange">{l.trigger || "—"}</td>
                      <td className="p-2 tabular-nums">{l.day || "—"}</td>
                      <td className="p-2 text-right tabular-nums">{l.summary?.dockets ?? "—"}</td>
                      <td className="p-2 text-right tabular-nums">{l.summary?.m3_delivered ?? "—"}</td>
                      <td className="p-2 text-[10px] text-muted-industrial truncate max-w-[280px]" title={(l.recipients || []).join(", ") || l.error || ""}>
                        {l.status === "sent" ? (l.recipients || []).join(", ") : (l.error || "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* History browser */}
      <div className="border border-industrial bg-surface" data-testid="test-history-browser">
        <div className="p-3 border-b border-industrial flex items-center gap-3 flex-wrap">
          <div className="label-tag flex items-center gap-1.5">
            <Database className="w-3 h-3" />Archived Snapshots
          </div>
          {batches.length > 0 ? (
            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger data-testid="history-batch-picker" className="rounded-none bg-transparent border-industrial mono text-xs h-8 w-72">
                <SelectValue placeholder="Pick an archive batch" />
              </SelectTrigger>
              <SelectContent>
                {batches.map((b) => (
                  <SelectItem key={b.batch_id} value={b.batch_id}>
                    {formatArchivedAt(b.archived_at)} · {b.total} records · {b.archived_by || "system"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-xs text-muted-industrial mono">No archive batches yet.</div>
          )}
          {batches.length > 0 && (
            <Select value={kind || "__all__"} onValueChange={(v) => setKind(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="history-kind-picker" className="rounded-none bg-transparent border-industrial mono text-xs h-8 w-40">
                <SelectValue placeholder="All kinds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All kinds</SelectItem>
                {Object.entries(KIND_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="ml-auto mono text-[10px] text-muted-industrial">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        </div>

        {currentBatch && (
          <div className="px-3 py-2 border-b border-industrial bg-surface-2 flex items-center gap-3 flex-wrap">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Batch counts</div>
            {Object.entries(currentBatch.counts).map(([k, n]) => (
              <div key={k} className="mono text-[10px] text-muted-industrial">
                {(KIND_META[k]?.label) || k}: <span className="text-primary-orange font-bold">{n}</span>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full mono text-xs">
            <thead className="sticky top-0 bg-surface-2 z-10">
              <tr className="border-b border-industrial">
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Kind</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Reference</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Date</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Customer</th>
                <th className="p-2 text-right uppercase text-[9px] tracking-widest text-muted-industrial">Amount / Qty</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Status</th>
              </tr>
            </thead>
            <tbody>
              {rowsLoading && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-industrial">Loading…</td></tr>
              )}
              {!rowsLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-industrial">No records in this batch.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} data-testid={`history-row-${i}`} className="border-b border-industrial hover:bg-surface-2">
                  <td className="p-2 uppercase text-[10px] text-primary-orange tabular-nums">{r._archive?.kind || "—"}</td>
                  <td className="p-2 truncate max-w-[220px]" title={r.invoice_no || r.docket_no || r.id}>{r.invoice_no || r.docket_no || (r.id || "").slice(0, 8)}</td>
                  <td className="p-2 text-muted-industrial tabular-nums">{r.date || r.created_at?.slice(0, 10) || "—"}</td>
                  <td className="p-2 truncate max-w-[220px]">{r.customer_name || "—"}</td>
                  <td className="p-2 text-right tabular-nums">
                    {r.total != null ? `$${Number(r.total).toFixed(2)}` : r.quantity_m3 != null ? `${r.quantity_m3} m³` : "—"}
                  </td>
                  <td className="p-2 text-muted-industrial uppercase text-[10px]">{r.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset confirmation dialog */}
      <Dialog open={resetOpen} onOpenChange={(v) => { if (!running) { setResetOpen(v); setResetResult(null); }}}>
        <DialogContent className="rounded-none max-w-lg bg-surface border-2 border-red-700 p-0" data-testid="live-launch-dialog">
          <DialogHeader className="p-5 border-b border-red-700 bg-red-950/30">
            <DialogTitle className="heading uppercase text-xl font-black flex items-center gap-2 text-red-400">
              <ShieldAlert className="w-5 h-5" />Confirm Live-Launch Reset
            </DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            {!resetResult && preview && (
              <>
                <div className="text-sm">
                  This will archive <span className="text-primary-orange font-bold">{preview.total} record{preview.total === 1 ? "" : "s"}</span> into <span className="mono text-primary-orange">test_history</span> and reset Invoice + Docket counters to <span className="text-primary-orange font-bold">0</span>.
                  It <b>cannot be undone</b> — the archive is your only rollback.
                </div>
                <div className="border border-industrial bg-surface-2 p-3 space-y-1">
                  {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([col, n]) => (
                    <div key={col} className="flex items-center justify-between mono text-xs">
                      <span className="text-muted-industrial uppercase text-[10px] tracking-widest">{col.replace(/_/g, " ")}</span>
                      <span className="text-primary-orange font-bold">{n}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="label-tag mb-1.5">Type <span className="text-red-400">LIVE-LAUNCH</span> to unlock</div>
                  <Input
                    data-testid="live-launch-confirm-input"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="LIVE-LAUNCH"
                    className="rounded-none bg-transparent border-red-700 mono text-lg h-11 tracking-widest"
                    autoFocus
                  />
                </div>
              </>
            )}
            {resetResult && (
              <div data-testid="live-launch-result">
                <div className="text-sm text-emerald-400 font-bold mono uppercase tracking-widest mb-2">Archive complete</div>
                <div className="border border-industrial bg-surface-2 p-3 mono text-xs space-y-1">
                  <div>Batch ID: <span className="text-primary-orange">{resetResult.batch_id.slice(0, 8)}</span></div>
                  <div>Total archived: <span className="text-primary-orange font-bold">{resetResult.total_moved}</span></div>
                  <div>Counters reset: <span className="text-primary-orange">{(resetResult.counters_reset || []).join(", ")}</span></div>
                </div>
                <div className="text-xs text-muted-industrial mt-2">Day-one invoice number is now <span className="text-primary-orange font-bold">#1</span>.</div>
              </div>
            )}
          </div>
          <div className="border-t border-industrial p-4 flex items-center justify-end gap-2 bg-surface-2">
            {!resetResult ? (
              <>
                <Button variant="ghost" disabled={running} onClick={() => { setResetOpen(false); setConfirmText(""); }} className="rounded-none uppercase text-xs tracking-widest">Cancel</Button>
                <Button
                  data-testid="live-launch-commit"
                  onClick={runReset}
                  disabled={running || confirmText !== "LIVE-LAUNCH"}
                  className="rounded-none bg-red-600 hover:bg-red-500 text-white uppercase text-xs tracking-widest font-bold"
                >
                  {running ? "Archiving…" : "Archive & Reset"}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </>
            ) : (
              <Button
                data-testid="live-launch-close"
                onClick={() => { setResetOpen(false); setResetResult(null); }}
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold"
              >Done</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmailDeliveryPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings/email/status").then((r) => setStatus(r.data)).catch(() => setStatus({ error: true }));
  }, []);

  const sendTest = async () => {
    if (!to.trim()) return toast.error("Enter an address to send the test to");
    setSending(true);
    try {
      const { data } = await api.post("/settings/email/test", { to: to.trim() });
      toast.success(`Sent via ${data.provider} from ${data.sender}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" && !detail.includes("<") ? detail : "Send failed — check the address and try again");
    } finally {
      setSending(false);
    }
  };

  const ok = status && !status.error && status.configured;

  return (
    <div className="border border-industrial bg-surface p-4" data-testid="email-delivery-panel">
      <div className="flex items-start gap-3">
        <Send className="w-5 h-5 text-primary-orange shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Outbound Mail</div>
          <h2 className="heading text-lg font-black uppercase mt-0.5">Email Delivery</h2>
          <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
            Shows which provider this environment is actually using and who emails are sent from. Fire a self-test to
            prove delivery end-to-end before relying on the nightly backup.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
            {[
              ["Provider", status?.provider || "—"],
              ["Sender", status?.sender || "—"],
              ["Configured", status ? (ok ? "Yes" : "No") : "—"],
              ["Fallback", status?.fallback_recipient || "none"],
            ].map(([l, v]) => (
              <div key={l} className="border border-industrial p-2 min-w-0">
                <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{l}</div>
                <div className={`mono text-xs font-bold mt-0.5 truncate ${l === "Configured" ? (ok ? "text-emerald-500" : "text-red-500") : "text-primary-orange"}`} title={String(v)} data-testid={`email-status-${l.toLowerCase()}`}>{String(v)}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end mt-3">
            <div className="flex-1">
              <div className="label-tag mb-1.5">Send a self-test to</div>
              <Input
                data-testid="email-test-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="you@yourcompany.com"
                className="rounded-none bg-transparent border-industrial mono text-xs h-9"
              />
            </div>
            <Button
              onClick={sendTest}
              disabled={sending}
              data-testid="email-test-send"
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold h-9"
            >{sending ? "Sending…" : "Send test email"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatArchivedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
}


// ── Restore Archived Day · v3.6w ──────────────────────────────────
// Copies every archived record whose plant-local date matches the
// chosen day back into its live collection. Used when the live-launch
// reset swept up real production data by accident.
function RestoreDayPanel() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);
  const [previewData, setPreviewData] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const preview = async () => {
    setPreviewData(null); setResult(null);
    try {
      const r = await api.post("/admin/restore-day", { date, confirm: "preview", dry_run: true });
      setPreviewData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const run = async () => {
    const expected = `RESTORE-${date}`;
    if (confirmText !== expected) return toast.error(`Type ${expected} to confirm`);
    setRunning(true);
    try {
      const r = await api.post("/admin/restore-day", { date, confirm: expected, dry_run: false });
      setResult(r.data);
      toast.success(`Restored records for ${date}`);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRunning(false); }
  };

  const totalFound = previewData
    ? Object.values(previewData.counts || {}).reduce((s, c) => s + (c.found || 0), 0)
    : 0;
  const totalToRestore = previewData
    ? Object.values(previewData.counts || {}).reduce((s, c) => s + Math.max(0, (c.found || 0) - (c.already_live || 0)), 0)
    : 0;

  return (
    <div className="border-2 border-amber-600 bg-amber-950/10 p-4" data-testid="restore-day-panel">
      <div className="flex items-start gap-3">
        <RotateCcw className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="mono text-[10px] uppercase tracking-widest text-amber-400 font-bold">Recover Real Data</div>
          <h2 className="heading text-lg font-black uppercase mt-0.5">Restore Archived Day</h2>
          <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
            Pulls jobs, dockets, invoices, batches, quotes, payments and EFT sales from
            <span className="mono text-primary-orange"> test_history</span> back into their live tables for the chosen day.
            Existing live records with the same id are never overwritten (skipped). The archive copies stay in place for audit.
          </p>

          <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end mt-4">
            <div>
              <div className="label-tag mb-1.5">Day to restore (plant local)</div>
              <Input
                type="date"
                data-testid="restore-day-date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setPreviewData(null); setResult(null); }}
                className="rounded-none bg-transparent border-industrial mono h-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={preview}
              data-testid="restore-day-preview"
              className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest h-9"
            >
              Preview
            </Button>
          </div>

          {previewData && (
            <div className="mt-4">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mb-1">
                Found {totalFound} archived record{totalFound === 1 ? "" : "s"} for {previewData.date} · {totalToRestore} ready to restore
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(previewData.counts || {}).map(([k, c]) => {
                  const meta = KIND_META[k] || { label: k, Icon: FileText };
                  return (
                    <div key={k} className="border border-amber-800/50 bg-black/40 p-2" data-testid={`restore-count-${k}`}>
                      <div className="flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-amber-400">
                        <meta.Icon className="w-2.5 h-2.5" />{meta.label}
                      </div>
                      <div className="mono text-xl font-black text-ink mt-0.5">{c.found}</div>
                      <div className="mono text-[9px] text-muted-industrial">
                        {c.already_live > 0 ? `${c.already_live} already live` : "all restorable"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalToRestore > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="label-tag">Type <span className="text-primary-orange">RESTORE-{date}</span> to confirm</div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="restore-day-confirm-input"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`RESTORE-${date}`}
                      className="rounded-none bg-transparent border-industrial mono uppercase"
                    />
                    <Button
                      onClick={run}
                      disabled={running || confirmText !== `RESTORE-${date}`}
                      data-testid="restore-day-run"
                      className="rounded-none bg-amber-600 hover:bg-amber-500 text-black uppercase text-xs tracking-widest font-bold"
                    >
                      {running ? "Restoring…" : `Restore ${totalToRestore} records`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="mt-4 border border-emerald-700 bg-emerald-950/20 p-3">
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Restore complete · {result.date}
              </div>
              <div className="mono text-xs mt-1">
                {Object.entries(result.counts || {}).map(([k, c]) => (
                  <span key={k} className="mr-3">{k}: <span className="text-primary-orange font-bold">{c.restored}</span></span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ── Void a Day's Invoices · v3.6z ───────────────────────────────────
// Marks every invoice issued on the chosen day as `voided`, unlinks
// their dockets so the next daily-run can rebuild them. Used when the
// original run bundled multiple customers under one bucket (e.g.
// "EFT") and you need to start over with clean per-customer grouping.
function VoidDayPanel() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);
  const [preview, setPreview] = useState(null);
  const [includePaid, setIncludePaid] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    setPreview(null); setResult(null);
    try {
      const r = await api.post("/invoices/void-day", { date, confirm: "preview", dry_run: true, include_paid: includePaid });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const run = async () => {
    const expected = `VOID-${date}`;
    if (confirmText !== expected) return toast.error(`Type ${expected} to confirm`);
    setRunning(true);
    try {
      const r = await api.post("/invoices/void-day", {
        date, confirm: expected, dry_run: false, include_paid: includePaid,
      });
      setResult(r.data);
      toast.success(`Voided ${r.data.voided_count} · Released ${r.data.dockets_released} dockets · $${r.data.money_voided.toFixed(2)}`);
      setConfirmText("");
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="border-2 border-red-600 bg-red-950/10 p-4" data-testid="void-day-panel">
      <div className="flex items-start gap-3">
        <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="mono text-[10px] uppercase tracking-widest text-red-400 font-bold">Reset Invoices</div>
          <h2 className="heading text-lg font-black uppercase mt-0.5">Void a Day's Invoices</h2>
          <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
            Marks every invoice issued on the chosen day as <span className="mono text-red-300">voided</span> and
            releases their dockets so the next daily-run can rebuild them cleanly (per-customer). Use this
            when an old run bunched multiple customers under one bucket. <span className="text-amber-300">Paid</span>
            invoices are skipped unless you tick the box.
          </p>

          <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end mt-4">
            <div>
              <div className="label-tag mb-1.5">Issued date to void (plant local)</div>
              <Input
                type="date"
                data-testid="void-day-date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setPreview(null); setResult(null); setConfirmText(""); }}
                className="rounded-none bg-transparent border-industrial mono h-9"
              />
            </div>
            <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-muted-industrial h-9">
              <input
                type="checkbox"
                checked={includePaid}
                onChange={(e) => setIncludePaid(e.target.checked)}
                data-testid="void-day-include-paid"
                className="accent-red-500"
              /> include paid
            </label>
            <Button
              variant="outline"
              onClick={doPreview}
              data-testid="void-day-preview"
              className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest h-9"
            >
              Preview
            </Button>
          </div>

          {preview && (
            <div className="mt-4">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mb-2">
                {preview.voided_count} invoice{preview.voided_count === 1 ? "" : "s"} · {preview.dockets_released} docket{preview.dockets_released === 1 ? "" : "s"} · <span className="text-red-300">${preview.money_voided.toFixed(2)}</span> total
                {preview.skipped_count > 0 && <span className="text-amber-300"> · {preview.skipped_count} skipped</span>}
              </div>
              {preview.voided.length > 0 && (
                <div className="mono text-[11px] max-h-40 overflow-y-auto border border-red-800/50 p-2 bg-black/40">
                  {preview.voided.slice(0, 20).map((v) => (
                    <div key={v.invoice_no} className="flex justify-between gap-2 py-0.5 border-b border-red-800/20 last:border-b-0" data-testid={`void-preview-${v.invoice_no}`}>
                      <span className="text-red-300 font-bold">{v.invoice_no}</span>
                      <span className="opacity-70 flex-1 truncate">{v.customer_name}</span>
                      <span className="opacity-70">{v.dockets_to_release} d</span>
                      <span className="text-red-300">${(v.total || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {preview.voided.length > 20 && <div className="opacity-60 mt-1">+{preview.voided.length - 20} more</div>}
                </div>
              )}
              {preview.skipped.length > 0 && (
                <div className="mono text-[11px] mt-2">
                  <div className="label-tag text-amber-400">Skipped</div>
                  {preview.skipped.map((s) => (
                    <div key={s.invoice_no} className="text-muted-industrial py-0.5">
                      <span className="text-amber-300">{s.invoice_no}</span> · {s.reason}
                    </div>
                  ))}
                </div>
              )}

              {preview.voided_count > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="label-tag">Type <span className="text-red-300">VOID-{date}</span> to confirm</div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="void-day-confirm-input"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`VOID-${date}`}
                      className="rounded-none bg-transparent border-industrial mono uppercase"
                    />
                    <Button
                      onClick={run}
                      disabled={running || confirmText !== `VOID-${date}`}
                      data-testid="void-day-run"
                      className="rounded-none bg-red-600 hover:bg-red-500 text-white uppercase text-xs tracking-widest font-bold"
                    >
                      {running ? "Voiding…" : `Void ${preview.voided_count} & Release Dockets`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="mt-4 border border-emerald-700 bg-emerald-950/20 p-3">
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Voided · {result.date}
              </div>
              <div className="mono text-xs mt-1">
                {result.voided_count} invoices · {result.dockets_released} dockets released · ${result.money_voided.toFixed(2)}
              </div>
              <div className="mono text-[10px] text-muted-industrial mt-1">
                Next step: go to <span className="text-primary-orange">Accounts → Daily Invoice Run</span> for {result.date} to rebuild them.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

